import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Card,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Button,
  LinearProgress,
  IconButton,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  LocationCity,
  ArrowBack,
  Description,
  InsertDriveFile,
  CheckCircle,
  Info,
  PlayArrow,
  AutoAwesome,
  Timeline,
  Settings,
  Delete,
  Edit,
  Download,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { projectsApi } from '../services/api';
import { Project, Document, ExtractionJobStatus } from '../types/project';
import DocumentUpload from '../components/DocumentUpload';
import RequirementsTable from '../components/RequirementsTable';
import SubmissionDocuments from '../components/SubmissionDocuments';
import ComplianceReview from '../components/ComplianceReview';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`project-tabpanel-${index}`}
      aria-labelledby={`project-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatCost = (cost: number): string => {
  return `$${cost.toFixed(4)}`;
};

const getFileIcon = (filename: string | undefined) => {
  if (!filename) {
    return <InsertDriveFile sx={{ color: 'text.secondary' }} />;
  }

  const extension = filename.split('.').pop()?.toLowerCase();
  switch (extension) {
    case 'pdf':
      return <Description sx={{ color: '#FF8C00' }} />;
    case 'doc':
    case 'docx':
      return <InsertDriveFile sx={{ color: 'primary.main' }} />;
    default:
      return <InsertDriveFile sx={{ color: 'text.secondary' }} />;
  }
};

// Function to load all requirements by paginating through API
const loadAllRequirements = async (projectId: string) => {
  let allRequirements: any[] = [];
  let currentPage = 1;
  let hasMorePages = true;

  while (hasMorePages) {
    const response = await projectsApi.getRequirements(projectId, currentPage, 100, 'all', 'all');

    if (response && response.requirements && Array.isArray(response.requirements)) {
      allRequirements = [...allRequirements, ...response.requirements];

      // Check if there are more pages
      if (response.pagination && response.pagination.has_next) {
        currentPage++;
      } else {
        hasMorePages = false;
      }
    } else {
      hasMorePages = false;
    }
  }

  return { requirements: allRequirements, total_count: allRequirements.length };
};

// Function to load only requirements that have compliance evaluation results
const loadComplianceRequirements = async (projectId: string) => {
  let allRequirements: any[] = [];
  let currentPage = 1;
  let hasMorePages = true;

  while (hasMorePages) {
    const response = await projectsApi.getRequirements(projectId, currentPage, 100, 'all', 'all');

    if (response && response.requirements && Array.isArray(response.requirements)) {
      // Filter to only include requirements that have compliance evaluation results
      const complianceRequirements = response.requirements.filter((req: any) =>
        req.compliance_evaluation || req.compliance_status || req.evidence_documents?.length > 0
      );

      allRequirements = [...allRequirements, ...complianceRequirements];

      // Check if there are more pages
      if (response.pagination && response.pagination.has_next) {
        currentPage++;
      } else {
        hasMorePages = false;
      }
    } else {
      hasMorePages = false;
    }
  }

  return { requirements: allRequirements, total_count: allRequirements.length };
};

export default function ProjectDashboard() {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [submissionDocuments, setSubmissionDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [extractionLoading, setExtractionLoading] = useState(false);
  const [extractionResults, setExtractionResults] = useState<any>(null);
  const [requirements, setRequirements] = useState<any[]>([]);
  const [complianceRequirements, setComplianceRequirements] = useState<any[]>([]);
  const [jobStatus, setJobStatus] = useState<ExtractionJobStatus | null>(null);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState<null | HTMLElement>(null);
  const [documentCosts, setDocumentCosts] = useState<any[]>([]);

  useEffect(() => {
    const fetchProjectData = async () => {
      if (!projectId) return;

      try {
        setLoading(true);
        setError(null);

        const [projectData, documentsData, submissionDocsData, savedRequirementsData, complianceRequirementsData, documentCostsData] = await Promise.all([
          projectsApi.getProject(projectId),
          projectsApi.getProjectDocuments(projectId),
          projectsApi.getSubmissionDocuments(projectId).catch(() => []), // Return empty array if no submission docs exist
          loadAllRequirements(projectId).catch(() => null), // Get all requirements for requirements table
          loadComplianceRequirements(projectId).catch(() => null), // Get only compliance-evaluated requirements
          projectsApi.getDocumentCosts(projectId).catch(() => ({ document_costs: [] })), // Return empty costs if none exist
        ]);

        setProject(projectData);
        setDocuments(documentsData);
        setSubmissionDocuments(submissionDocsData);
        setDocumentCosts(documentCostsData.document_costs || []);

        // Load saved requirements if they exist (for Requirements Extraction tab)
        if (savedRequirementsData && savedRequirementsData.requirements && Array.isArray(savedRequirementsData.requirements)) {
          const formattedRequirements = savedRequirementsData.requirements.map((req: any, index: number) => ({
            id: req.id,
            title: req.title,
            requirement_type: req.requirement_type || 'Other',
            category: req.category || 'General',
            description: req.description,
            numeric_value: req.measurement_value || null, // Use measurement_value from backend
            unit: req.unit,
            source_document_name: req.source_document_name || null,
            source_section: req.source_section || null,
            source_page: req.source_page_start || 1,
            source_text: req.description,
            confidence: req.confidence_score,
            district: Array.isArray(req.zoning_districts) ? req.zoning_districts.join(', ') : '',
            zoning_districts: req.zoning_districts || [],
            zoning_district_names: req.zoning_district_names || {},
            applies_to: Array.isArray(req.applies_to) ? req.applies_to.join(', ') : '',
            measurement_method: req.measurement_method,
            tolerance: req.tolerance,
            confidence_score: req.confidence_score,
            approved: req.approved || req.validation_status === "approved" || false,
            // Include compliance fields
            compliance_status: req.compliance_status || null,
            compliance_notes: req.compliance_notes || null,
            compliance_confidence: req.compliance_confidence || null,
            // NEW: Include evidence documents and compliance evaluation
            evidence_documents: req.evidence_documents || [],
            compliance_evaluation: req.compliance_evaluation || null
          }));

          setRequirements(formattedRequirements);
          setExtractionResults(savedRequirementsData);
        }

        // Load compliance requirements separately (for Review Compliance tab)
        if (complianceRequirementsData && complianceRequirementsData.requirements && Array.isArray(complianceRequirementsData.requirements)) {
          const formattedComplianceRequirements = complianceRequirementsData.requirements.map((req: any, index: number) => ({
            id: req.id,
            title: req.title,
            requirement_type: req.requirement_type || 'Other',
            category: req.category || 'General',
            description: req.description,
            numeric_value: req.measurement_value || null,
            unit: req.unit,
            source_document_name: req.source_document_name || null,
            source_section: req.source_section || null,
            source_page: req.source_page_start || 1,
            source_text: req.description,
            confidence: req.confidence_score,
            district: Array.isArray(req.zoning_districts) ? req.zoning_districts.join(', ') : '',
            zoning_districts: req.zoning_districts || [],
            zoning_district_names: req.zoning_district_names || {},
            applies_to: Array.isArray(req.applies_to) ? req.applies_to.join(', ') : '',
            measurement_method: req.measurement_method,
            tolerance: req.tolerance,
            confidence_score: req.confidence_score,
            approved: req.approved || req.validation_status === "approved" || false,
            // Include compliance fields
            compliance_status: req.compliance_status || null,
            compliance_notes: req.compliance_notes || null,
            compliance_confidence: req.compliance_confidence || null,
            // Include evidence documents and compliance evaluation
            evidence_documents: req.evidence_documents || [],
            compliance_evaluation: req.compliance_evaluation || null
          }));

          setComplianceRequirements(formattedComplianceRequirements);
        }
      } catch (err) {
        console.error('Error fetching project data:', err);
        setError('Failed to load project data');
      } finally {
        setLoading(false);
      }
    };

    fetchProjectData();
  }, [projectId]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearTimeout(pollingInterval);
      }
    };
  }, [pollingInterval]);

  // Start polling for job status with progressive backoff
  const startJobPolling = (jobId: string) => {
    setExtractionLoading(true);

    // Clear any existing polling
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }

    pollJobStatus(jobId, 0);
  };

  const pollJobStatus = async (jobId: string, pollCount: number = 0) => {
    try {
      const status = await projectsApi.getJobStatus(jobId);
      console.log('Job status update:', status); // Debug logging
      setJobStatus(status);

      if (status.status === 'completed') {
        // Job completed successfully
        setExtractionLoading(false);

        if (status.results) {
          setExtractionResults(status.results);

          // Format requirements
          if (status.results.requirements && Array.isArray(status.results.requirements)) {
            const formattedRequirements = status.results.requirements.map((req: any, index: number) => ({
              id: req.id,
              title: req.title || req.name || 'Unnamed Requirement',
              description: req.description || '',
              requirement_type: req.requirement_type || 'Other',
              category: req.category || 'Other',
              numeric_value: req.measurement_value || null, // Use measurement_value from backend
              unit: req.unit || null,
              source_document_name: req.source_document_name || null,
              source_section: req.source_section || null,
              source_page: req.source_page_start || 1,
              source_text: req.description || null,
              confidence: req.confidence_score || null,
              zoning_districts: req.zoning_districts || [],
              zoning_district_names: req.zoning_district_names || {},
              approved: false,
              isCustom: false
            }));
            setRequirements(formattedRequirements);
          }
        }

        // Refresh documents to get updated ai_processed status
        if (projectId) {
          const updatedDocuments = await projectsApi.getProjectDocuments(projectId);
          setDocuments(updatedDocuments);
        }

      } else if (status.status === 'failed') {
        // Job failed
        setExtractionLoading(false);
        setError(`Extraction failed: ${status.progress_message || 'Unknown error'}`);
      } else {
        // Progressive backoff: 2s -> 5s -> 10s -> 15s (max)
        const delays = [2000, 5000, 10000, 15000];
        const delay = delays[Math.min(pollCount, delays.length - 1)];

        const timeoutId = setTimeout(() => pollJobStatus(jobId, pollCount + 1), delay);
        setPollingInterval(timeoutId as any); // Store timeout ID for cleanup
      }
    } catch (err) {
      console.error('Error polling job status:', err);
      setExtractionLoading(false);
      setError('Failed to get extraction status');
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleBackToProjects = () => {
    navigate('/projects');
  };

  const handleUploadComplete = async () => {
    if (!projectId) return;

    setDocumentsLoading(true);
    try {
      const updatedDocuments = await projectsApi.getProjectDocuments(projectId);
      setDocuments(updatedDocuments);
    } catch (err) {
      console.error('Error refreshing documents:', err);
    } finally {
      setDocumentsLoading(false);
    }
  };

  const handleSubmissionUploadComplete = async () => {
    if (!projectId) return;

    setDocumentsLoading(true);
    try {
      const updatedSubmissionDocs = await projectsApi.getSubmissionDocuments(projectId);
      setSubmissionDocuments(updatedSubmissionDocs);
    } catch (err) {
      console.error('Error refreshing submission documents:', err);
    } finally {
      setDocumentsLoading(false);
    }
  };

  const handleDoneUploading = () => {
    setTabValue(1); // Switch to Requirements Extraction tab
  };

  const handleNextToSubmission = () => {
    setTabValue(2); // Switch to Submission Documents tab
  };

  const handleNextToReview = () => {
    setTabValue(3); // Switch to Review Compliance tab
  };

  const handleStartExtraction = async () => {
    if (!projectId) return;

    // Clear all previous state
    setError(null);
    setJobStatus(null);
    setExtractionResults(null);

    try {
      console.log('Starting extraction for project:', projectId);
      const startResponse = await projectsApi.startExtraction(projectId);
      console.log('Start response:', startResponse);

      if (startResponse.status === 'already_completed') {
        setError('All documents have already been processed');
        return;
      }

      if (startResponse.status === 'started' && startResponse.job_id) {
        console.log(`Started extraction job ${startResponse.job_id} for ${startResponse.documents_count} documents`);
        console.log(`Estimated completion: ${startResponse.estimated_duration_minutes} minutes`);

        // Start polling for job status
        startJobPolling(startResponse.job_id);
      }
    } catch (err) {
      console.error('Error starting requirements extraction:', err);
      setError('Failed to start extraction. Please try again.');
      setExtractionLoading(false);
    }
  };


  // Function to refresh requirements data from API (includes compliance data)
  const refreshRequirements = async () => {
    if (!project) return;

    try {
      // Refresh all requirements for Requirements Extraction tab
      const savedRequirementsData = await loadAllRequirements(project.id);
      if (savedRequirementsData && savedRequirementsData.requirements && Array.isArray(savedRequirementsData.requirements)) {
        const formattedRequirements = savedRequirementsData.requirements.map((req: any, index: number) => ({
          id: req.id,
          title: req.title,
          requirement_type: req.requirement_type || 'Other',
          category: req.category || 'General',
          description: req.description,
          numeric_value: req.measurement_value || null,
          unit: req.unit,
          source_document_name: req.source_document_name || null,
          source_section: req.source_section || null,
          source_page: req.source_page_start || 1,
          source_text: req.description,
          confidence: req.confidence_score,
          district: Array.isArray(req.zoning_districts) ? req.zoning_districts.join(', ') : '',
          zoning_districts: req.zoning_districts || [],
          zoning_district_names: req.zoning_district_names || {},
          applies_to: req.applies_to || [],
          approved: req.approved,
          validation_status: req.validation_status,
          // Include compliance fields
          compliance_status: req.compliance_status || null,
          compliance_notes: req.compliance_notes || null,
          compliance_confidence: req.compliance_confidence || null,
          // NEW: Include evidence documents and compliance evaluation
          evidence_documents: req.evidence_documents || [],
          compliance_evaluation: req.compliance_evaluation || null
        }));
        setRequirements(formattedRequirements);
      }

      // Refresh compliance requirements for Review Compliance tab
      const complianceRequirementsData = await loadComplianceRequirements(project.id);
      if (complianceRequirementsData && complianceRequirementsData.requirements && Array.isArray(complianceRequirementsData.requirements)) {
        const formattedComplianceRequirements = complianceRequirementsData.requirements.map((req: any, index: number) => ({
          id: req.id,
          title: req.title,
          requirement_type: req.requirement_type || 'Other',
          category: req.category || 'General',
          description: req.description,
          numeric_value: req.measurement_value || null,
          unit: req.unit,
          source_document_name: req.source_document_name || null,
          source_section: req.source_section || null,
          source_page: req.source_page_start || 1,
          source_text: req.description,
          confidence: req.confidence_score,
          district: Array.isArray(req.zoning_districts) ? req.zoning_districts.join(', ') : '',
          zoning_districts: req.zoning_districts || [],
          zoning_district_names: req.zoning_district_names || {},
          applies_to: req.applies_to || [],
          approved: req.approved,
          validation_status: req.validation_status,
          // Include compliance fields
          compliance_status: req.compliance_status || null,
          compliance_notes: req.compliance_notes || null,
          compliance_confidence: req.compliance_confidence || null,
          // Include evidence documents and compliance evaluation
          evidence_documents: req.evidence_documents || [],
          compliance_evaluation: req.compliance_evaluation || null
        }));
        setComplianceRequirements(formattedComplianceRequirements);
      }
    } catch (error) {
      console.error('Failed to refresh requirements:', error);
    }
  };

  const handleComplianceUpdate = async (requirementId: string, status: string, notes?: string) => {
    if (!project) {
      console.error('Project not found');
      setError('Project not found. Please refresh the page.');
      return;
    }

    try {
      // Update the requirement compliance status via API
      await projectsApi.updateRequirementCompliance(project.id, requirementId, status, notes);

      // Update local state after successful API call - update both requirements and compliance requirements
      const updatedRequirements = requirements.map(req =>
        req.id === requirementId
          ? { ...req, compliance_status: status, compliance_notes: notes }
          : req
      );
      setRequirements(updatedRequirements);

      const updatedComplianceRequirements = complianceRequirements.map(req =>
        req.id === requirementId
          ? { ...req, compliance_status: status, compliance_notes: notes }
          : req
      );
      setComplianceRequirements(updatedComplianceRequirements);
    } catch (error) {
      console.error('Failed to update requirement compliance:', error);
      setError('Failed to update requirement compliance. Please try again.');
    }
  };

  const handleSettingsClick = (event: React.MouseEvent<HTMLElement>) => {
    setSettingsAnchorEl(event.currentTarget);
  };

  const handleSettingsClose = () => {
    setSettingsAnchorEl(null);
  };

  const handleEditProject = () => {
    handleSettingsClose();
    // TODO: Implement edit project functionality
    console.log('Edit project clicked');
  };

  const handleDeleteProject = async () => {
    handleSettingsClose();
    if (!project) return;

    if (window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      try {
        await projectsApi.deleteProject(project.id);
        navigate('/projects');
      } catch (err) {
        console.error('Error deleting project:', err);
        setError('Failed to delete project');
      }
    }
  };

  const handleExportData = () => {
    handleSettingsClose();
    // TODO: Implement export functionality
    console.log('Export project data clicked');
  };

  const handleDeleteDocument = async (documentId: string, documentName: string) => {
    if (!project) return;

    try {
      setError(null);

      // First, get the requirements count for this document
      const requirementsInfo = await projectsApi.getDocumentRequirementsCount(project.id, documentId);

      let confirmMessage: string;

      if (requirementsInfo.requirement_count === 0) {
        // Simple confirmation for unprocessed documents
        confirmMessage = `Are you sure you want to delete "${documentName}"?\n\nThis will permanently delete the file. This action cannot be undone.`;
      } else {
        // Enhanced warning for processed documents with requirements
        const sampleList = requirementsInfo.sample_requirement_titles.length > 0
          ? `\n\nSome requirements that will be deleted:\n• ${requirementsInfo.sample_requirement_titles.join('\n• ')}${requirementsInfo.requirement_count > 3 ? `\n• ... and ${requirementsInfo.requirement_count - 3} more` : ''}`
          : '';

        confirmMessage = `⚠️ WARNING: Delete processed document?\n\n"${documentName}" has been analyzed and contains ${requirementsInfo.requirement_count} extracted requirement${requirementsInfo.requirement_count !== 1 ? 's' : ''}.\n\nDeleting this document will PERMANENTLY remove:\n• The document file\n• All ${requirementsInfo.requirement_count} extracted requirements\n• Processing history and metadata${sampleList}\n\nThis action cannot be undone.\n\nAre you sure you want to proceed?`;
      }

      if (window.confirm(confirmMessage)) {
        await projectsApi.deleteDocument(project.id, documentId);

        // Refresh the document list
        const updatedDocuments = await projectsApi.getProjectDocuments(project.id);
        setDocuments(updatedDocuments);

        // If this document had requirements, refresh the requirements list too
        try {
          const [savedRequirementsData, complianceRequirementsData] = await Promise.all([
            loadAllRequirements(project.id),
            loadComplianceRequirements(project.id)
          ]);

          if (savedRequirementsData && savedRequirementsData.requirements && Array.isArray(savedRequirementsData.requirements)) {
            const formattedRequirements = savedRequirementsData.requirements.map((req: any, index: number) => ({
              id: req.id,
              title: req.title,
              requirement_type: req.requirement_type || 'Other',
              category: req.category || 'General',
              description: req.description,
              numeric_value: req.measurement_value || null,
              unit: req.unit,
              source_document_name: req.source_document_name || null,
              source_section: req.source_section || null,
              source_page: req.source_page_start || 1,
              source_text: req.description,
              confidence: req.confidence_score,
              district: Array.isArray(req.zoning_districts) ? req.zoning_districts.join(', ') : '',
              zoning_districts: req.zoning_districts || [],
              zoning_district_names: req.zoning_district_names || {},
              applies_to: Array.isArray(req.applies_to) ? req.applies_to.join(', ') : '',
              measurement_method: req.measurement_method,
              tolerance: req.tolerance,
              confidence_score: req.confidence_score,
              approved: req.approved || req.validation_status === "approved" || false,
              // Include compliance fields
              compliance_status: req.compliance_status || null,
              compliance_notes: req.compliance_notes || null,
              compliance_confidence: req.compliance_confidence || null,
              // NEW: Include evidence documents and compliance evaluation
              evidence_documents: req.evidence_documents || [],
              compliance_evaluation: req.compliance_evaluation || null
            }));
            setRequirements(formattedRequirements);
            setExtractionResults(savedRequirementsData);

            // Also update compliance requirements
            if (complianceRequirementsData && complianceRequirementsData.requirements && Array.isArray(complianceRequirementsData.requirements)) {
              const formattedComplianceRequirements = complianceRequirementsData.requirements.map((req: any, index: number) => ({
                id: req.id,
                title: req.title,
                requirement_type: req.requirement_type || 'Other',
                category: req.category || 'General',
                description: req.description,
                numeric_value: req.measurement_value || null,
                unit: req.unit,
                source_document_name: req.source_document_name || null,
                source_section: req.source_section || null,
                source_page: req.source_page_start || 1,
                source_text: req.description,
                confidence: req.confidence_score,
                district: Array.isArray(req.zoning_districts) ? req.zoning_districts.join(', ') : '',
                zoning_districts: req.zoning_districts || [],
                zoning_district_names: req.zoning_district_names || {},
                applies_to: Array.isArray(req.applies_to) ? req.applies_to.join(', ') : '',
                measurement_method: req.measurement_method,
                tolerance: req.tolerance,
                confidence_score: req.confidence_score,
                approved: req.approved || req.validation_status === "approved" || false,
                // Include compliance fields
                compliance_status: req.compliance_status || null,
                compliance_notes: req.compliance_notes || null,
                compliance_confidence: req.compliance_confidence || null,
                // Include evidence documents and compliance evaluation
                evidence_documents: req.evidence_documents || [],
                compliance_evaluation: req.compliance_evaluation || null
              }));
              setComplianceRequirements(formattedComplianceRequirements);
            } else {
              setComplianceRequirements([]);
            }
          } else {
            // No requirements left after deletion
            setRequirements([]);
            setComplianceRequirements([]);
            setExtractionResults(null);
          }
        } catch (reqErr) {
          // Requirements refresh failed - set to empty
          setRequirements([]);
          setComplianceRequirements([]);
          setExtractionResults(null);
        }

        console.log(`Successfully deleted document: ${documentName}`);
      }
    } catch (err) {
      console.error('Error deleting document:', err);
      setError(`Failed to delete "${documentName}". Please try again.`);
    }
  };

  // Check if there are unprocessed documents
  const hasUnprocessedDocuments = documents.some(doc => !doc.ai_processed);

  // Determine if extraction is needed
  const needsExtraction = documents.length > 0 && (requirements.length === 0 || hasUnprocessedDocuments);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (error || !project) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
        <Container maxWidth="xl" sx={{ py: 8 }}>
          <Alert severity="error">
            {error || 'Project not found'}
          </Alert>
          <Button onClick={handleBackToProjects} sx={{ mt: 2 }}>
            Back to Projects
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      {/* Header */}
      <Box sx={{ bgcolor: 'white', boxShadow: 1 }}>
        <Container maxWidth="xl">
          <Box sx={{ py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationCity sx={{ color: 'primary.main', fontSize: 32 }} />
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    bgcolor: 'secondary.main',
                    borderRadius: '50%',
                  }}
                />
                <Box
                  sx={{
                    width: 9,
                    height: 9,
                    bgcolor: 'primary.main',
                    borderRadius: '2px',
                    transform: 'rotate(45deg)',
                  }}
                />
              </Box>
              <Typography variant="h5" sx={{ ml: 2, fontWeight: 700, color: 'primary.main' }}>
                CivilSteve
              </Typography>
              <Typography variant="body2" sx={{ ml: 1, color: 'text.secondary', fontWeight: 500 }}>
                {project.name} • {project.municipality}, {project.state}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                onClick={handleSettingsClick}
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main' }
                }}
                size="small"
              >
                <Settings />
              </IconButton>

              <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={handleBackToProjects}
                sx={{ fontWeight: 500 }}
              >
                Back to Projects
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: 4 }}>

        {/* Done Button - Only show on Compliance Documents tab */}
        {tabValue === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              onClick={handleDoneUploading}
              sx={{
                px: 4,
                py: 1.5,
                fontWeight: 600,
                fontSize: '1.1rem',
                borderRadius: 2,
                textTransform: 'none',
              }}
            >
              Done Uploading
            </Button>
          </Box>
        )}

        {/* Tabs */}
        <Paper elevation={0} sx={{ border: 1, borderColor: 'grey.300' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="project tabs">
              <Tab label="Compliance Documents" />
              <Tab label="Requirements Extraction" />
              <Tab label="Submission Documents" />
              <Tab label="Review Compliance" />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            {/* Compliance Documents Tab */}
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Compliance Documents
              </Typography>

              <DocumentUpload
                projectId={project.id}
                onUploadComplete={handleUploadComplete}
              />

              {(documents.length > 0 || documentsLoading) && (
                <Box sx={{ mt: 4 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Uploaded Compliance Documents
                  </Typography>

                  {documentsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <Card elevation={0} sx={{ border: 1, borderColor: 'grey.300' }}>
                      <List>
                        {documents.map((doc, index) => (
                          <ListItem
                            key={doc.id}
                            divider={index < documents.length - 1}
                          >
                            <ListItemIcon>
                              {getFileIcon(doc.name)}
                            </ListItemIcon>
                            <ListItemText
                              primary={doc.name}
                              secondary={
                                <Box sx={{ mt: 0.5 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    {formatFileSize(doc.size)} • Uploaded {formatDate(doc.uploaded_at)}
                                  </Typography>
                                  <Box sx={{ mt: 0.5 }}>
                                    <Chip
                                      label={doc.status}
                                      size="small"
                                      color={doc.status === 'uploaded' ? 'success' : 'default'}
                                      variant="outlined"
                                    />
                                  </Box>
                                </Box>
                              }
                              secondaryTypographyProps={{ component: 'div' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Card>
                  )}
                </Box>
              )}
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {/* Requirements Extraction Tab */}
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                AI Requirements Extraction
              </Typography>

              {documents.length === 0 ? (
                <Alert severity="info" sx={{ mb: 3 }}>
                  Upload ordinance documents in the Compliance Documents tab to begin requirements extraction.
                </Alert>
              ) : (
                <Box>
                  {/* Status Header */}
                  <Card elevation={0} sx={{ border: 1, borderColor: needsExtraction ? 'grey.300' : 'success.main', p: 3, mb: 3 }}>
                    <Grid container spacing={3} alignItems="center">
                      <Grid item xs={12} md={8}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          <Box sx={{ color: needsExtraction ? 'secondary.main' : 'success.main' }}>
                            {needsExtraction ? <AutoAwesome sx={{ fontSize: 28 }} /> : <CheckCircle sx={{ fontSize: 28 }} />}
                          </Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {needsExtraction ? 'Ready for AI Analysis' : 'Analysis Complete'}
                          </Typography>
                        </Box>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                          {extractionLoading && jobStatus
                            ? jobStatus.progress_message || 'Processing documents...'
                            : needsExtraction
                            ? (() => {
                                const unprocessedCount = documents.filter(doc => !doc.ai_processed).length;
                                return `${hasUnprocessedDocuments && requirements.length > 0 ? 'New documents added. ' : ''}${unprocessedCount} compliance document${unprocessedCount !== 1 ? 's' : ''} ready for ${project.ordinance_type ? `${project.ordinance_type.toLowerCase()} ordinance` : 'ordinance'} analysis using Anthropic Claude 4.0.`;
                              })()
                            : `Extracted ${requirements.length} requirements from ${documents.filter(doc => doc.ai_processed).length} processed document${documents.filter(doc => doc.ai_processed).length !== 1 ? 's' : ''}.`
                          }
                        </Typography>

                        {/* Progress Bar */}
                        {extractionLoading && jobStatus && jobStatus.progress_total > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                Progress: {jobStatus.progress_current} / {jobStatus.progress_total} documents
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {Math.round((jobStatus.progress_current / jobStatus.progress_total) * 100)}%
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={(jobStatus.progress_current / jobStatus.progress_total) * 100}
                              sx={{ height: 8, borderRadius: 4 }}
                            />
                          </Box>
                        )}
                        <Chip
                          label={extractionLoading
                            ? `Analysis Status: ${jobStatus?.status === 'processing' ? 'Processing' : 'Starting'}`
                            : needsExtraction
                            ? "Analysis Status: Pending"
                            : "Analysis Status: Complete"
                          }
                          color={extractionLoading
                            ? "info"
                            : needsExtraction
                            ? "warning"
                            : "success"
                          }
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                          <Button
                            variant="contained"
                            color="secondary"
                            size="large"
                            startIcon={extractionLoading ? <CircularProgress size={20} color="inherit" /> : <PlayArrow />}
                            onClick={handleStartExtraction}
                            disabled={extractionLoading || !needsExtraction}
                            sx={{
                              px: 3,
                              py: 1.5,
                              fontWeight: 600,
                              borderRadius: 2,
                              textTransform: 'none',
                            }}
                          >
                            {extractionLoading
                              ? jobStatus?.status === 'processing'
                                ? `Processing... (${jobStatus.progress_current}/${jobStatus.progress_total})`
                                : 'Starting...'
                              : needsExtraction
                              ? 'Start Extraction'
                              : 'Complete'
                            }
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Card>


                  {/* Document List - Compact */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                      Documents:
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {documents.map((doc) => {
                        const docCost = documentCosts.find(cost => cost.document_id === doc.id);
                        return (
                          <Box key={doc.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {getFileIcon(doc.name)}
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {doc.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ({formatFileSize(doc.size)})
                              </Typography>
                              {docCost && doc.ai_processed && (
                                <Typography variant="caption" sx={{
                                  color: 'primary.main',
                                  fontWeight: 600,
                                  backgroundColor: 'primary.50',
                                  px: 0.75,
                                  py: 0.25,
                                  borderRadius: 1,
                                  fontSize: '0.7rem'
                                }}>
                                  {formatCost(docCost.processing_cost_usd)}
                                </Typography>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                label={doc.ai_processed ? "Analyzed" : "Ready"}
                                size="small"
                                color={doc.ai_processed ? "primary" : "success"}
                                variant="outlined"
                              />
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteDocument(doc.id, doc.name)}
                                sx={{
                                  color: doc.ai_processed ? 'warning.main' : 'error.main',
                                  '&:hover': {
                                    backgroundColor: doc.ai_processed ? 'warning.50' : 'error.50'
                                  }
                                }}
                                title={doc.ai_processed
                                  ? `⚠️ Delete analyzed document "${doc.name}" (will remove extracted requirements)`
                                  : `Delete "${doc.name}"`
                                }
                              >
                                <Delete sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>

                  {/* Results Area */}
                  {extractionResults && requirements.length > 0 ? (
                    <Box>
                      <RequirementsTable
                        projectId={project.id}
                      />

                      {/* Next Step Button */}
                      <Box sx={{ mt: 4, textAlign: 'center' }}>
                        <Button
                          variant="contained"
                          color="primary"
                          size="large"
                          onClick={handleNextToSubmission}
                          sx={{
                            px: 4,
                            py: 1.5,
                            fontWeight: 600,
                            borderRadius: 2,
                            textTransform: 'none',
                          }}
                        >
                          Next: Upload Submission Documents
                        </Button>
                      </Box>
                    </Box>
                  ) : extractionResults && requirements.length === 0 ? (
                    <Card elevation={0} sx={{ border: 1, borderColor: 'warning.main', p: 3, textAlign: 'center' }}>
                      <Box sx={{ color: 'warning.main', mb: 2 }}>
                        <Info sx={{ fontSize: 48 }} />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'warning.main' }}>
                        No Requirements Found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        The AI analysis completed but found no extractable requirements. You can add custom requirements manually.
                      </Typography>
                      <RequirementsTable
                        projectId={project.id}
                      />
                    </Card>
                  ) : (
                    <Card elevation={0} sx={{ border: 2, borderColor: 'grey.200', borderStyle: 'dashed', p: 4, textAlign: 'center' }}>
                      <Box sx={{ color: 'text.secondary', mb: 2 }}>
                        <Timeline sx={{ fontSize: 48 }} />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                        Extracted Requirements Will Appear Here
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Once analysis is complete, all identified ordinance requirements will be displayed in an organized, actionable format.
                      </Typography>
                    </Card>
                  )}
                </Box>
              )}
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            {/* Submission Documents Tab */}
            <Box sx={{ p: 3 }}>
              <SubmissionDocuments
                projectId={project.id}
                submissionDocuments={submissionDocuments}
                onUploadComplete={handleSubmissionUploadComplete}
                loading={documentsLoading}
                onNextToReview={handleNextToReview}
              />
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            {/* Review Compliance Tab */}
            <Box sx={{ p: 3 }}>
              <ComplianceReview
                projectId={project.id}
                requirements={complianceRequirements}
                submissionDocuments={submissionDocuments}
                onComplianceUpdate={handleComplianceUpdate}
                onRequirementsRefresh={refreshRequirements}
              />
            </Box>
          </TabPanel>
        </Paper>
      </Container>

      {/* Settings Menu */}
      <Menu
        anchorEl={settingsAnchorEl}
        open={Boolean(settingsAnchorEl)}
        onClose={handleSettingsClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleEditProject}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Project</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleExportData}>
          <ListItemIcon>
            <Download fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export Data</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleDeleteProject} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <Delete fontSize="small" sx={{ color: 'error.main' }} />
          </ListItemIcon>
          <ListItemText>Delete Project</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}